import { useEffect, useRef, useState } from "react";
import { MicrophoneStage, MicrophoneSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * "Hey CFO" wake-word listener.
 * Uses the browser Web Speech API (webkitSpeechRecognition) with continuous
 * recognition. When the recognized text contains one of the trigger phrases,
 * it invokes `onWake()`.
 *
 * Enabling state persisted in localStorage so the user's choice survives
 * page reloads.
 */
const TRIGGERS = [
  "hey cfo",
  "hey seafoam",   // common Whisper mis-hearing
  "oye cfo",
  "hola cfo",
  "hey ceo",       // yet another common mishear
];

export default function WakeWord({ onWake }) {
  const [enabled, setEnabled] = useState(
    () => window.localStorage.getItem("cfo_wake_enabled") === "1",
  );
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recRef = useRef(null);
  const lastFireRef = useRef(0);

  // Testability + external triggers: dispatch a `cfo:wake` CustomEvent
  // (with optional `detail` = command string) to programmatically simulate
  // the wake word firing. Also used by unit tests and Playwright.
  useEffect(() => {
    const handler = (e) => {
      const cmd = typeof e.detail === "string" ? e.detail : "";
      onWake?.(cmd);
    };
    window.addEventListener("cfo:wake", handler);
    return () => window.removeEventListener("cfo:wake", handler);
  }, [onWake]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
  }, []);

  useEffect(() => {
    window.localStorage.setItem("cfo_wake_enabled", enabled ? "1" : "0");
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !supported) {
      // Stop any existing listener
      if (recRef.current) {
        try { recRef.current.stop(); } catch { /* ignore */ }
        recRef.current = null;
      }
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const rec = new SR();
    rec.lang = "es-ES";
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 3;

    rec.onstart = () => setListening(true);
    rec.onerror = (e) => {
      // "no-speech" / "aborted" happen constantly on empty rooms, ignore.
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast.error("Micrófono bloqueado — no puedo escuchar 'Hey CFO'");
        setEnabled(false);
      }
    };
    rec.onend = () => {
      setListening(false);
      // Restart automatically until the user disables it explicitly
      if (recRef.current === rec) {
        try { rec.start(); } catch { /* browser may throttle */ }
      }
    };
    rec.onresult = (event) => {
      const now = Date.now();
      if (now - lastFireRef.current < 3000) return;   // debounce
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        for (let j = 0; j < result.length; j++) {
          const txt = (result[j].transcript || "").toLowerCase().trim();
          if (!txt) continue;
          // Find any trigger + capture command tail (text after the trigger word)
          for (const trig of TRIGGERS) {
            const idx = txt.indexOf(trig);
            if (idx === -1) continue;
            const tail = txt
              .substring(idx + trig.length)
              .replace(/^[,\.\s\?!]+/, "")
              .trim();
            // Fire immediately once the utterance is final OR the tail is
            // substantial enough that we're confident the user finished asking.
            const shouldFire = result.isFinal || tail.length > 10;
            if (shouldFire) {
              lastFireRef.current = now;
              if (tail) {
                toast.success(`🎙 "${tail.slice(0, 60)}"`);
              } else {
                toast.success("👋 CFO activado");
              }
              onWake?.(tail);
              return;
            }
          }
        }
      }
    };

    recRef.current = rec;
    try {
      rec.start();
    } catch (err) {
      // Browsers restrict when another recognizer is running.
    }
    return () => {
      recRef.current = null;
      try { rec.stop(); } catch { /* ignore */ }
    };
  }, [enabled, supported, onWake]);

  if (!supported) return null;

  return (
    <button
      data-testid="wake-word-toggle"
      onClick={() => setEnabled((v) => !v)}
      title={
        enabled
          ? listening
            ? "Escuchando 'Hey CFO'... clic para apagar"
            : "Wake-word activo — reiniciando..."
          : "Activar 'Hey CFO' — di la frase y se abre el chat"
      }
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] font-mono uppercase tracking-widest font-bold transition ${
        enabled
          ? listening
            ? "border-emerald-500/60 bg-emerald-500/12 text-emerald-300 animate-pulse"
            : "border-white/15 bg-white/6 text-white/60"
          : "border-white/10 bg-transparent text-white/40 hover:text-white/70 hover:border-white/25"
      }`}
    >
      {enabled ? (
        <MicrophoneStage size={11} weight={listening ? "fill" : "bold"} />
      ) : (
        <MicrophoneSlash size={11} weight="bold" />
      )}
      Hey CFO
    </button>
  );
}
